import React, { useState, useMemo } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Select,
  Tag,
  message,
  PageHeader,
  Typography,
} from 'antd';
import { PlusOutlined, EditOutlined } from '@ant-design/icons';
import moment from 'moment';

import {
  useAdminProcessors,
  PROCESSOR_PRODUCT_CURRENCY_ROWS,
  PROCESSOR_KEYS,
  processorDocumentId,
} from '../../../models/useAdminProcessors';

const { Paragraph } = Typography;

const STATUS_OPTIONS = ['active', 'inactive'];

export default function AdminProcessorsScreen() {
  const { rows, loading, existingIds, saveProcessor } = useAdminProcessors();
  const [modalOpen, setModalOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [form] = Form.useForm();

  const addableCombos = useMemo(
    () => PROCESSOR_PRODUCT_CURRENCY_ROWS.filter((row) => !existingIds.has(processorDocumentId(row.product, row.currency))),
    [existingIds],
  );

  const openAdd = () => {
    if (addableCombos.length === 0) {
      message.info(
        'All known product and currency combinations already have a row. Edit an existing row or extend the list in code.',
      );
      return;
    }
    setEditingRecord(null);
    form.resetFields();
    const first = addableCombos[0];
    form.setFieldsValue({
      comboKey: `${first.product}|${first.currency}`,
      processor: first.defaultProcessor,
      status: 'active',
    });
    setModalOpen(true);
  };

  const openEdit = (record) => {
    setEditingRecord(record);
    form.resetFields();
    form.setFieldsValue({
      processor: record.processor,
      status: record.status || 'inactive',
    });
    setModalOpen(true);
  };

  const onComboChange = (comboKey) => {
    const row = PROCESSOR_PRODUCT_CURRENCY_ROWS.find((r) => `${r.product}|${r.currency}` === comboKey);
    if (row) {
      form.setFieldsValue({ processor: row.defaultProcessor });
    }
  };

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      setSubmitting(true);
      if (editingRecord) {
        await saveProcessor({
          id: editingRecord.id,
          product: editingRecord.product,
          currency: editingRecord.currency,
          processor: values.processor,
          status: values.status,
        });
      } else {
        const [product, currency] = values.comboKey.split('|');
        await saveProcessor({
          product,
          currency,
          processor: values.processor,
          status: values.status,
        });
      }
      setModalOpen(false);
      setEditingRecord(null);
      form.resetFields();
    } catch (e) {
      if (e?.errorFields) return;
      console.error(e);
      message.error(e?.message || 'Save failed');
    } finally {
      setSubmitting(false);
    }
  };

  const columns = [
    {
      title: 'Document ID',
      dataIndex: 'id',
      key: 'id',
      render: (id) => <code>{id}</code>,
    },
    { title: 'Product', dataIndex: 'product', key: 'product' },
    { title: 'Currency', dataIndex: 'currency', key: 'currency' },
    {
      title: 'Processor',
      dataIndex: 'processor',
      key: 'processor',
      render: (p) => <Tag color="blue">{p}</Tag>,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (s) => (
        <Tag color={s === 'active' ? 'green' : 'default'}>{s || '—'}</Tag>
      ),
    },
    {
      title: 'Updated',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      render: (d) => (d && moment(d).isValid() ? moment(d).format('YYYY-MM-DD HH:mm') : '—'),
    },
    {
      title: '',
      key: 'actions',
      render: (_, record) => (
        <Button type="link" icon={<EditOutlined />} onClick={() => openEdit(record)}>
          Edit
        </Button>
      ),
    },
  ];

  return (
    <div style={{ height: '100%', textAlign: 'center' }}>
      <div style={{ textAlign: 'left' }}>
        <PageHeader
          ghost={false}
          title="Payment processors"
          onBack={() => window.history.back()}
          extra={
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={openAdd}
              disabled={addableCombos.length === 0}
            >
              Processor
            </Button>
          }
          footer={
            <Paragraph
              type="secondary"
              style={{
                margin: 0,
                maxWidth: '100%',
                whiteSpace: 'normal',
                overflow: 'visible',
              }}
            >
              Firestore collection processors — one document per product and currency (active rules
              override static defaults).
            </Paragraph>
          }
        />
      </div>

      <div style={{ padding: '0 24px 24px', textAlign: 'left' }}>
        <Table
          rowKey="id"
          loading={loading}
          dataSource={rows}
          columns={columns}
          pagination={{ pageSize: 20 }}
        />
      </div>

      <Modal
        title={editingRecord ? `Edit ${editingRecord.id}` : 'Add processor row'}
        visible={modalOpen}
        onOk={handleOk}
        onCancel={() => {
          setModalOpen(false);
          setEditingRecord(null);
          form.resetFields();
        }}
        confirmLoading={submitting}
        destroyOnClose
        width={480}
      >
        <Form form={form} layout="vertical">
          {!editingRecord && (
            <Form.Item
              name="comboKey"
              label="Product and currency"
              rules={[{ required: true, message: 'Choose a combination' }]}
            >
              <Select
                placeholder="Select product × currency"
                onChange={onComboChange}
                options={addableCombos.map((r) => ({
                  value: `${r.product}|${r.currency}`,
                  label: `${r.product} / ${r.currency}`,
                }))}
              />
            </Form.Item>
          )}
          <Form.Item
            name="processor"
            label="Processor"
            rules={[{ required: true, message: 'Select processor' }]}
          >
            <Select
              options={PROCESSOR_KEYS.map((k) => ({ value: k, label: k }))}
            />
          </Form.Item>
          <Form.Item
            name="status"
            label="Status"
            rules={[{ required: true, message: 'Select status' }]}
          >
            <Select
              options={STATUS_OPTIONS.map((s) => ({ value: s, label: s }))}
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
